import { ChangeEvent, useCallback, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  MdCalendarMonth,
  MdContactPhone,
  MdEmail,
  MdHouse,
  MdInfo,
  MdLocationCity,
  MdLock,
  MdMap,
  MdNote,
  MdNumbers,
  MdPerson,
  MdPhone,
  MdStreetview,
  MdVisibility,
  MdVisibilityOff,
  MdVpnKey,
} from 'react-icons/md';
import Skeleton from 'react-loading-skeleton';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  IconButton,
  TextField,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Tooltip,
  Button,
  Typography,
} from '@mui/material';
import { getCEPAddress, register } from 'api/api';
import Checkbox from 'components/Checkbox';
import ErrorMessage from 'components/ErrorMessage';
import IconDialog from 'components/IconDialog';
import { RegisterFormProps, UserPayloadRequestDTO } from 'dtos/AuthenticationDTO';
import { useFormik } from 'formik';
import { registerFormSchema } from 'shared/validators';
import {
  Container,
  AppHeader,
  LoginLogo,
  LoginCardBody,
  Span2,
  CardLogin,
  AppLogo,
  LoginCardHeader,
  NeedsValidationGrid,
  LoginCardFooter,
  MaterialLink,
} from 'styles/Styles';
import { OrganizadorStatusProps } from 'types/organizadorStatus';
import { AppError } from 'utils/AppError';
import { convertStringToDate } from 'utils/convertStringToDate';
import { handleApiError } from 'utils/handleApiError';
import { maskAddressNumber } from 'utils/MaskAddressNumber';
import { maskCEP } from 'utils/MaskCep';
import { maskCpf } from 'utils/MaskCpf';
import { maskDate } from 'utils/MaskDate';
import { maskName } from 'utils/MaskName';
import { maskPhoneNumber } from 'utils/MaskPhoneNumber';
import { unmaskCEP } from 'utils/UnMaskCep';
import { unmaskCpf } from 'utils/UnMaskCpf';
import { unmaskPhoneNumber } from 'utils/UnMaskPhoneNumber';
import { getBackgroundImage, getLogo, getSlugFromURL } from 'utils/whiteLabel';

import { version } from '../../constants';

interface RegisterProps {
  headerColor?: string;
  btnColor?: string;
}

export default function Register(props: RegisterProps) {
  const [organizadorStatus, setOrganizadorStatus] =
    useState<OrganizadorStatusProps>('nao_solicitado');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSearchingCep, setIsSearchingCep] = useState(false);
  const PROJECT = getSlugFromURL();

  const { push } = useHistory();

  const reRef = useRef<ReCAPTCHA>();

  const initialValues: RegisterFormProps = {
    fullName: '',
    email: '',
    password: '',
    passwordConfirmation: '',
    cpf: '',
    cnpj: '',
    cep: '',
    birthDate: '',
    sex: 'masculino',
    firstPhone: '',
    secondPhone: '',
    street: '',
    neighborhood: '',
    city: '',
    state: '',
    complement: '',
    number: '',
    organizador_status: 'nao_solicitado',
  };

  const {
    values,
    errors,
    isSubmitting,
    handleChange,
    setFieldValue,
    handleSubmit,
    setSubmitting,
    resetForm,
  } = useFormik({
    validationSchema: registerFormSchema,
    initialValues,
    onSubmit: (values: RegisterFormProps) => {
      handleSubmitRegisterForm(values);
    },
    validateOnChange: false,
    validateOnBlur: false,
  });

  const handleSubmitRegisterForm = async (fields: RegisterFormProps) => {
    try {
      const reToken = await reRef.current?.executeAsync();
      reRef?.current?.reset();

      if (reToken) {
        if (/\s/.test(fields.password) || /\s/.test(fields.passwordConfirmation)) {
          toast.error('Senha não deve conter espaços.');
          return;
        }

        const payload: UserPayloadRequestDTO = {
          nome: fields.fullName.trim(),
          password: fields.password,
          email: fields.email.trim(),
          cpf: unmaskCpf(fields.cpf),
          data_nascimento: convertStringToDate(fields.birthDate),
          sexo: fields.sex,
          telefone: unmaskPhoneNumber(fields.firstPhone),
          telefone_2: fields.secondPhone ? unmaskPhoneNumber(fields.secondPhone) : undefined,
          cep: unmaskCEP(fields.cep),
          bairro: fields.neighborhood.trim(),
          logradouro: fields.street.trim(),
          complemento: fields.complement && fields.complement.trim(),
          numero: fields.number,
          cidade: fields.city,
          estado: fields.state,
          organizador_status: fields.organizador_status,
          reToken,
        };

        await register(payload);

        if (organizadorStatus === 'nao_solicitado') {
          toast.success('Cadastro realizado com sucesso!');
          resetForm();
          push('/login');
        }

        if (organizadorStatus === 'pendente') {
          setIsModalOpen(true);
        }
      }
    } catch (err) {
      const error = err as AppError;

      handleApiError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSearchCep = useCallback(
    async (cep: string) => {
      if (cep.length !== 9) return;

      try {
        setIsSearchingCep(true);

        const response = await getCEPAddress(cep);

        if (response) {
          setFieldValue('street', response.data.logradouro);
          setFieldValue('neighborhood', response.data.bairro);
          setFieldValue('city', response.data.localidade);
          setFieldValue('state', response.data.uf);
        }
      } catch (err) {
        setFieldValue('street', '');
        setFieldValue('neighborhood', '');
        setFieldValue('city', '');
        setFieldValue('state', '');

        const error = err as AppError;

        toast.error(error.data);
      } finally {
        setIsSearchingCep(false);
      }
    },
    [values?.cep],
  );

  const handleSetCPF = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const maskedCpf = maskCpf(e.target.value);
    setFieldValue('cpf', maskedCpf);
  };

  const handleOrganizadorStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setOrganizadorStatus('pendente');
      setFieldValue('organizador_status', 'pendente');
    } else {
      setOrganizadorStatus('nao_solicitado');
      setFieldValue('organizador_status', 'nao_solicitado');
    }
  };

  const handleConfirmModal = () => {
    toast.success('Cadastro realizado com sucesso!');
    resetForm();
    push('/login');
  };

  // TODO: Texto dos botões não está centralizado

  return (
    <>
      <Container style={{ backgroundImage: `url(${getBackgroundImage(PROJECT)})` }}>
        <AppHeader>
          <CardLogin>
            <LoginLogo>
              <AppLogo src={getLogo(PROJECT)} height="135" alt="logo" />
            </LoginLogo>
            <LoginCardHeader style={{ backgroundColor: props.headerColor ?? '#0071bc' }}>
              Cadastro
            </LoginCardHeader>
            <LoginCardBody>
              <NeedsValidationGrid
                onSubmit={handleSubmit}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                  }
                }}
              >
                <div>
                  <TextField
                    id="input-fullName"
                    label="Nome completo"
                    placeholder="João Pereira Cunha"
                    fullWidth
                    required
                    value={values?.fullName}
                    error={!!errors?.fullName}
                    onChange={(e) => {
                      const maskedName = maskName(e.target.value);
                      setFieldValue('fullName', maskedName);
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MdPerson />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {errors?.fullName && <ErrorMessage error={errors?.fullName} />}
                </div>

                <div>
                  <TextField
                    id="input-cpf"
                    label="CPF"
                    placeholder="599.010.830-36"
                    fullWidth
                    required
                    value={values?.cpf}
                    error={!!errors?.cpf}
                    onChange={handleSetCPF}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MdNote />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {errors?.cpf && <ErrorMessage error={errors?.cpf} />}
                </div>

                <Span2>
                  <TextField
                    id="input-email"
                    type="email"
                    fullWidth
                    required
                    value={values?.email}
                    error={!!errors?.email}
                    onChange={handleChange('email')}
                    label="Email"
                    placeholder="joao@gmail.com"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MdEmail />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {errors?.email && <ErrorMessage error={errors?.email} />}
                </Span2>

                <Span2>
                  <TextField
                    id="input-password"
                    fullWidth
                    required
                    value={values?.password}
                    error={!!errors?.password}
                    onChange={(e) => setFieldValue('password', e.target.value.replace(/\s/g, ''))}
                    label="Senha"
                    placeholder="Abc12345"
                    type={showPassword ? 'text' : 'password'}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MdVpnKey />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip
                            style={{ cursor: 'default', margin: '0 5px' }}
                            title="Deve conter pelo menos uma letra maiúscula, uma letra minúscula, um número e entre 8 a 20 caracteres"
                          >
                            <div>
                              <MdInfo color="rgba(0, 0, 0, 0.54)" />
                            </div>
                          </Tooltip>
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <MdVisibility /> : <MdVisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {errors?.password && <ErrorMessage error={errors?.password} />}
                </Span2>

                <Span2>
                  <TextField
                    id="input-password-confirmation"
                    fullWidth
                    required
                    value={values?.passwordConfirmation}
                    error={!!errors?.passwordConfirmation}
                    type={showPasswordConfirmation ? 'text' : 'password'}
                    label="Confirmar senha"
                    placeholder="Abc12345"
                    onChange={(e) =>
                      setFieldValue('passwordConfirmation', e.target.value.replace(/\s/g, ''))
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MdLock />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
                          >
                            {showPasswordConfirmation ? <MdVisibility /> : <MdVisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {errors?.passwordConfirmation && (
                    <ErrorMessage error={errors?.passwordConfirmation} />
                  )}
                </Span2>

                <div>
                  <TextField
                    id="input-cep"
                    label="CEP"
                    placeholder="36570-977"
                    fullWidth
                    required
                    value={values?.cep}
                    error={!!errors?.cep}
                    onChange={(e) => {
                      const maskedCep = maskCEP(e.target.value);
                      setFieldValue('cep', maskedCep);
                      e.target.value.length === 9 && handleSearchCep(e.target.value);
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MdHouse />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {errors?.cep && <ErrorMessage error={errors?.cep} />}
                </div>

                {isSearchingCep ? (
                  <Skeleton height={40} />
                ) : (
                  <div>
                    <TextField
                      id="input-state"
                      label="Estado (UF)"
                      placeholder="MG"
                      fullWidth
                      required
                      disabled
                      value={values?.state}
                      error={!!errors?.state}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MdMap />
                          </InputAdornment>
                        ),
                      }}
                    />
                    {errors?.state && <ErrorMessage error={errors?.state} />}
                  </div>
                )}

                {isSearchingCep ? (
                  <Skeleton height={40} />
                ) : (
                  <div>
                    <TextField
                      id="input-city"
                      label="Cidade"
                      placeholder="Belo Horizonte"
                      fullWidth
                      required
                      disabled
                      value={values?.city}
                      error={!!errors?.city}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MdLocationCity />
                          </InputAdornment>
                        ),
                      }}
                    />
                    {errors?.city && <ErrorMessage error={errors?.city} />}
                  </div>
                )}

                <div>
                  <TextField
                    id="input-neighborhood"
                    fullWidth
                    required
                    value={values?.neighborhood}
                    error={!!errors?.neighborhood}
                    onChange={handleChange('neighborhood')}
                    label="Bairro"
                    placeholder="Centro"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MdLocationCity />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {errors?.neighborhood && <ErrorMessage error={errors?.neighborhood} />}
                </div>

                <Span2>
                  <TextField
                    id="input-street"
                    label="Logradouro"
                    placeholder="Rua dos Estudantes"
                    fullWidth
                    required
                    value={values?.street}
                    error={!!errors?.street}
                    onChange={handleChange('street')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MdStreetview />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {errors?.street && <ErrorMessage error={errors?.street} />}
                </Span2>

                <div>
                  <TextField
                    id="input-number"
                    fullWidth
                    value={values?.number}
                    onChange={(e) => {
                      const maskedNumber = maskAddressNumber(e.target.value);
                      setFieldValue('number', maskedNumber);
                    }}
                    label="Número"
                    placeholder="100"
                    error={!!errors?.number}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MdNumbers />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {errors?.number && <ErrorMessage error={errors?.number} />}
                </div>

                <div>
                  <TextField
                    id="input-complement"
                    fullWidth
                    value={values?.complement}
                    error={!!errors?.complement}
                    onChange={handleChange('complement')}
                    label="Complemento"
                    placeholder="Bloco A, apto 1007"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MdLocationCity />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {errors?.number && <ErrorMessage error={errors?.number} />}
                </div>

                <div>
                  <TextField
                    id="input-phone-1"
                    fullWidth
                    required
                    value={values?.firstPhone}
                    error={!!errors?.firstPhone}
                    onChange={(e) => {
                      const maskedPhoneNumber = maskPhoneNumber(e.target.value);
                      setFieldValue('firstPhone', maskedPhoneNumber);
                    }}
                    label="Telefone/WhatsApp"
                    placeholder="(31) 9 9999-9999"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MdPhone />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {errors?.firstPhone && <ErrorMessage error={errors?.firstPhone} />}
                </div>

                <div>
                  <TextField
                    id="input-phone-2"
                    fullWidth
                    value={values?.secondPhone}
                    error={!!errors?.secondPhone}
                    onChange={(e) => {
                      const maskedPhoneNumber = maskPhoneNumber(e.target.value);
                      setFieldValue('secondPhone', maskedPhoneNumber);
                    }}
                    label="Telefone 2 com DDD"
                    placeholder="(31) 9 9999-9999"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MdContactPhone />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {errors?.secondPhone && <ErrorMessage error={errors?.secondPhone} />}
                </div>

                <>
                  <FormControl fullWidth>
                    <InputLabel id="label-sexo">Sexo</InputLabel>
                    <Select
                      id="input-sex"
                      fullWidth
                      required
                      value={values?.sex}
                      error={!!errors?.sex}
                      placeholder="Masculino"
                      label="Sexo"
                      labelId="label-sexo"
                      defaultValue="masculino"
                      onChange={() => handleChange('sex')}
                    >
                      <MenuItem value="sexo" disabled>
                        Selecione seu sexo
                      </MenuItem>
                      <MenuItem value="masculino">Masculino</MenuItem>
                      <MenuItem value="feminino">Feminino</MenuItem>
                      <MenuItem value="outro">Outro</MenuItem>
                    </Select>
                  </FormControl>

                  <div>
                    <TextField
                      id="input-birthdate"
                      fullWidth
                      required
                      value={values?.birthDate}
                      error={!!errors?.birthDate}
                      onChange={(e) => {
                        const maskedDate = maskDate(e.target.value);
                        setFieldValue('birthDate', maskedDate);
                      }}
                      label="Data de nascimento"
                      placeholder="01/01/1999"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MdCalendarMonth />
                          </InputAdornment>
                        ),
                      }}
                    />

                    {errors?.birthDate && <ErrorMessage error={errors?.birthDate} />}
                  </div>
                </>

                <Span2>
                  <Checkbox
                    label="Quero ser um organizador de eventos"
                    checked={organizadorStatus === 'pendente'}
                    handleChange={handleOrganizadorStatusChange}
                  />
                </Span2>

                <Span2>
                  <Checkbox
                    label={
                      <span>
                        Aceito os{' '}
                        <MaterialLink
                          target={'_blank'}
                          href={'./' + PROJECT + '/files/TERMOS-DE-USO.pdf'}
                          variant="caption"
                        >
                          Termos de Uso
                        </MaterialLink>{' '}
                        e{' '}
                        <MaterialLink
                          target={'_blank'}
                          href={'./' + PROJECT + '/files/POLITICA-DE-PRIVACIDADE.html'}
                          variant="caption"
                        >
                          Política de Privacidade
                        </MaterialLink>
                      </span>
                    }
                    checked={acceptTerms}
                    handleChange={() => setAcceptTerms(!acceptTerms)}
                  />
                </Span2>

                <ReCAPTCHA
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY as string}
                  size="invisible"
                  ref={reRef as React.RefObject<ReCAPTCHA>}
                />

                <Span2 className="gap-2 d-grid">
                  <Button
                    color="primary"
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={isSubmitting || !acceptTerms}
                  >
                    {!isSubmitting ? (
                      <Typography fontSize="16px" fontWeight={600} letterSpacing="0.01em">
                        CADASTRAR
                      </Typography>
                    ) : (
                      <CircularProgress color="inherit" size={24} />
                    )}
                  </Button>
                </Span2>
              </NeedsValidationGrid>
            </LoginCardBody>
            <LoginCardFooter>
              <div className="d-flex justify-content-center">
                <Link to="/login">Já possui uma conta?</Link>
              </div>
              <div className="mt-3 d-flex justify-content-center">
                <Typography variant="caption" color="textSecondary">
                  {`versão: ${version}`}
                </Typography>
              </div>
            </LoginCardFooter>
          </CardLogin>
        </AppHeader>
      </Container>

      {isModalOpen && (
        <IconDialog
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onConfirm={handleConfirmModal}
          title="Sucesso!"
          description="Você receberá uma confirmação para se tornar organizador por email em até 14 dias"
          variant="success"
          color="success"
        />
      )}
    </>
  );
}
